import { useState, type FormEvent } from "react";
import { Icon } from "../components/Icon";
import { Notice, useNotice } from "../components/Notice";
import { PageHeader } from "../components/PageHeader";
import { Chip, EmptyState, ErrorState, Skeleton } from "../components/States";
import {
  agreementPdf,
  cancelSigning,
  quoteSigning,
  resendSignedCopy,
  resendSigning,
  sendForSigning,
  type SendResult,
  type SigningInfo,
  type SigningRequestView,
} from "../lib/agreements-data";
import { BRAND } from "../lib/config";
import { aud, fmtDate, fmtPlainDate, humanise } from "../lib/format";
import { openBlobInNewTab } from "../lib/jobs-data";
import { useAsync } from "../lib/useAsync";
import { QuoteEditor } from "./QuoteEditor";

const termsName = BRAND.termsTitle.charAt(0).toLowerCase() + BRAND.termsTitle.slice(1);
const live = (r: SigningRequestView) => (r.status === "sent" || r.status === "viewed") && !r.expired;

export function QuoteView({ id, edit }: { id: string; edit: boolean }) {
  const { data, error, loading, reload } = useAsync(() => quoteSigning(id), [id]);
  if (loading && !data) return <section className="page"><Skeleton rows={4} columns={3} /></section>;
  if (error) return <section className="page"><ErrorState message={error} onRetry={reload} /></section>;
  if (!data) return <section className="page"><EmptyState icon="search" title="Quote not found"><a href="#/quotes">Back to quotes</a></EmptyState></section>;
  if (edit && !data.agreement) return <QuoteEditor existing={data.quote} />;
  return <QuoteDetail info={data} reload={reload} />;
}

function QuoteDetail({ info, reload }: { info: SigningInfo; reload: () => void }) {
  const { quote: q, requests, agreement } = info;
  const current = requests.find(live);
  const { notice, setNotice, fail, ok } = useNotice();
  const [sent, setSent] = useState<SendResult | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  async function run(key: string, fn: () => Promise<void>) {
    setBusy(key);
    setNotice(null);
    try {
      await fn();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(null);
    }
  }

  const afterSend = (r: SendResult) => {
    setSent(r);
    if (r.emailed) ok(`Signing link emailed. It works until ${fmtDate(r.expiresAt)}.`);
    else setNotice({ tone: "error", text: `${r.emailError ?? "The email did not send."} Copy the link below and send it yourself, for example by text or WhatsApp.` });
    reload();
  };

  return (
    <section className="page job-grid" aria-labelledby="page-title">
      <a className="backlink" href="#/quotes"><Icon name="arrow" size={16} /> All quotes</a>
      <PageHeader
        eyebrow={`Quote ${q.quoteNumber} · ${agreement ? "Signed" : current ? "Awaiting signature" : humanise(q.status)}`}
        title={q.jobTitle ?? q.quoteNumber}
        meta={[q.clientName, q.siteAddress].filter(Boolean).join(" · ") || undefined}
        actions={!agreement && <a className="btn btn--outline" href={`#/quotes/${q.id}/edit`}>Edit quote</a>}
      />
      <Notice notice={notice} onDismiss={() => setNotice(null)} />

      {BRAND.agreements && (
        <div className={`card${agreement ? "" : " card--alert"}`}>
          <div className="card__head"><h2 className="card__title">Signature</h2>{agreement ? <Chip tone="green">Signed</Chip> : current ? <Chip tone="accent">Awaiting signature</Chip> : <Chip tone="outline">Not sent</Chip>}</div>
          <div className="card__body stack">
            {agreement && (
              <>
                <p>
                  Signed by <strong>{agreement.signerName}</strong>, {agreement.signerPosition}, for <strong>{agreement.companyLegalName}</strong> (ABN {agreement.companyAbn.replace(/^(\d{2})(\d{3})(\d{3})(\d{3})$/, "$1 $2 $3 $4")}) on {agreement.signedAtLocal}.
                </p>
                <p className="small muted">Document fingerprint (SHA-256): <span className="mono">{agreement.documentSha256}</span></p>
                <div className="form-actions">
                  <button type="button" className="btn btn--ink btn--sm" disabled={busy !== null} onClick={() => void run("pdf", () => openBlobInNewTab(() => agreementPdf(agreement.id)))}>
                    <Icon name="file" size={16} /> {busy === "pdf" ? "Opening" : "Open signed PDF"}
                  </button>
                  <button type="button" className="btn btn--outline btn--sm" disabled={busy !== null} onClick={() => void run("copy", async () => { await resendSignedCopy(agreement.id); ok(`Signed copy emailed again to ${agreement.signerEmail} and the office.`); })}>
                    {busy === "copy" ? "Sending" : "Email the signed copy again"}
                  </button>
                </div>
              </>
            )}

            {!agreement && current && (
              <>
                <p>
                  Sent to <strong>{current.recipientName}</strong> ({current.recipientEmail}) on {fmtDate(current.sentAt)}.{" "}
                  {current.viewedAt ? `Opened ${fmtDate(current.viewedAt)}.` : "Not opened yet."} The link works until {fmtDate(current.expiresAt)}.
                </p>
                <div className="form-actions">
                  <button type="button" className="btn btn--ink btn--sm" disabled={busy !== null} onClick={() => void run("resend", async () => afterSend(await resendSigning(current.id)))}>
                    {busy === "resend" ? "Sending" : "Send a new link"}
                  </button>
                  <button type="button" className="btn btn--outline btn--sm" disabled={busy !== null} onClick={() => void run("cancel", async () => { await cancelSigning(current.id); ok("Link cancelled. It no longer works."); reload(); })}>
                    Cancel the link
                  </button>
                </div>
              </>
            )}

            {!agreement && !current && <SendForm quote={q} busy={busy !== null} onSend={(name, email) => run("send", async () => afterSend(await sendForSigning(q.id, name, email)))} />}

            {sent && !agreement && (
              <div className="stack" style={{ gap: 6 }}>
                <span className="field__label">Signing link (shown once)</span>
                <div className="addcrew">
                  <input className="input" readOnly value={sent.link} onFocus={(e) => e.target.select()} style={{ minWidth: 0, flex: 1 }} />
                  <button type="button" className="btn btn--outline btn--sm" onClick={() => void navigator.clipboard.writeText(sent.link).then(() => ok("Link copied."), fail)}>Copy link</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="card">
        <div className="card__head"><h2 className="card__title">The quote</h2><Chip value={q.status} /></div>
        <div className="card__body stack">
          <dl className="facts">
            <div className="fact"><dt>Client</dt><dd>{q.clientName ?? "Not set"}{q.clientContact ? `, ${q.clientContact}` : ""}</dd></div>
            <div className="fact"><dt>Email</dt><dd>{q.clientEmail ?? "Not set"}</dd></div>
            <div className="fact"><dt>Crew</dt><dd>{q.crewSummary ?? "Not set"}</dd></div>
            <div className="fact"><dt>Valid until</dt><dd>{q.validUntil ? fmtPlainDate(q.validUntil) : "Not set"}</dd></div>
            <div className="fact fact--wide"><dt>Dates and times</dt><dd>{q.schedule ?? "Not set"}</dd></div>
          </dl>
          <div className="table-wrap table-wrap--flat">
            <table className="table table--stack">
              <thead><tr><th scope="col">Description</th><th scope="col">Qty</th><th scope="col" className="n">Rate ex GST</th><th scope="col" className="n">Amount</th></tr></thead>
              <tbody>
                {q.lines.map((l) => (
                  <tr key={l.sortOrder}><td data-label="Description">{l.description}</td><td data-label="Qty">{l.quantity} {l.unit}</td><td data-label="Rate" className="n">{aud(l.unitPrice)}</td><td data-label="Amount" className="n">{aud(l.amount)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="strong">Subtotal {aud(q.subtotal)} · GST {aud(q.gst)} · Total {aud(q.total)}</p>
          {q.notes && <p className="small">{q.notes}</p>}
        </div>
      </div>

      {requests.length > 0 && (
        <div className="card">
          <div className="card__head"><h2 className="card__title">Signing history</h2></div>
          <ul className="list">
            {requests.map((r) => (
              <li key={r.id} className="list__row">
                <span>{r.recipientName} ({r.recipientEmail}) <span className="sub">sent {fmtDate(r.sentAt)}{r.viewedAt ? `, opened ${fmtDate(r.viewedAt)}` : ""}{r.signedAt ? `, signed ${fmtDate(r.signedAt)}` : ""}</span></span>
                <Chip tone={r.status === "signed" ? "green" : live(r) ? "accent" : "outline"}>{r.expired ? "Expired" : humanise(r.status)}</Chip>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function SendForm({ quote, busy, onSend }: { quote: SigningInfo["quote"]; busy: boolean; onSend: (name: string, email: string) => void }) {
  const [name, setName] = useState(quote.clientContact ?? "");
  const [email, setEmail] = useState(quote.clientEmail ?? "");
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (name.trim() && email.trim()) onSend(name.trim(), email.trim());
  };
  return (
    <form className="stack" onSubmit={submit}>
      <p>Send this quote and your {termsName} for signing. The client reviews it, fills in their company details and signs online. Shifts for this quote stay unconfirmed until it is signed.</p>
      <div className="form-grid">
        <label className="field"><span className="field__label">Their name</span><input className="input" required value={name} onChange={(e) => setName(e.target.value)} /></label>
        <label className="field"><span className="field__label">Their email</span><input className="input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} /></label>
      </div>
      <div><button type="submit" className="btn btn--primary" disabled={busy} aria-busy={busy}>{busy ? "Sending" : "Send for signing"}</button></div>
    </form>
  );
}
